import debounce from "lodash.debounce";
import CardHeader from "@/components/Includes/CardHeader/CardHeader.vue";
import DefaultTools from "@/components/Includes/DefaultTools/DefaultTools.vue";
import Loader from "@/components/Includes/Loader/Loader.vue";
import Pagination from "@/components/Includes/Pagination/Pagination.vue";
import TableEmpty from "@/components/Includes/Table/TableFooter/TableEmpty/TableEmpty.vue";
import ITableCol from "@/interfaces/ITableCol";
import IMultiSelectItem from "@/interfaces/selects/IMultiSelectItem";
import getTableColumns from "@/libs/getTableColumns";
import { Component, Vue, Watch } from "vue-property-decorator";
import { namespace, Action, Getter } from "vuex-class";
import IMailSubject from "@/interfaces/IMailSubject";
import IPayloadQuery from "@/interfaces/IPayloadQuery";
import IClientApp from "@/interfaces/IClientApp";
import urlToId from "@/libs/urlToId";

const mailSubjectModule = namespace("mailSubject");

@Component({
  components: {
    Pagination,
    CardHeader,
    DefaultTools,
    TableEmpty,
    Loader,
  },
})
export default class MailSubjects extends Vue {
  @Action private clientAppsList!: (query: IPayloadQuery) => Promise<void>;

  @Getter private getClientApps!: () => IClientApp[];

  @mailSubjectModule.Action
  private readonly fetchMailSubjects!: (params: {
    query: string;
  }) => Promise<{ links: number; data: IMailSubject[] }>;

  @mailSubjectModule.Action
  private readonly fetchMailSubject!: (params: {
    id: number;
  }) => Promise<IMailSubject>;

  private page = 1;
  private limit = "100";
  private totalItems = 0;
  private mailSubjects: IMailSubject[] = [];
  private isLoading = false;

  private selectedTableColumns: IMultiSelectItem[] = [];

  private get tableColumns(): ITableCol[] {
    const columns = getTableColumns(this.mailSubjects, false);

    return Array.isArray(columns)
      ? columns.filter((column) => column.key !== "body")
      : [];
  }

  private get columns() {
    return this.tableColumns;
  }

  private get fields() {
    return this.tableColumns.filter((column) =>
      this.selectedTableColumns.some((s) => s.value === column.key)
    );
  }

  private onRowClicked({ id }: IMailSubject) {
    this.$router.push({
      name: "mail_subject",
      params: {
        id: `${id}`,
      },
    });
  }

  private goToCreateMailSubject() {
    this.$router.push({ name: "mail_subject_create" });
  }

  private async filterData(idVal: string) {
    this.isLoading = true;
    const id = +idVal;

    if (id > 0) {
      try {
        const mailSubject = await this.fetchMailSubject({ id });

        this.mailSubjects = [mailSubject];
      } catch {
        this.mailSubjects = [];
      }
    } else if (id === 0) {
      await this.getMailSubjects();
    }

    this.isLoading = false;
  }

  private getClientApp(val: string | null) {
    if (val) {
      const clientApp = this.getClientApps().find(
        ({ id }) => id === +urlToId(val)
      );

      if (clientApp) {
        return clientApp.title;
      }
    }
  }

  @Watch("page", { immediate: true })
  @Watch("limit")
  private async getMailSubjects() {
    this.isLoading = true;
    try {
      const { data, links } = await this.fetchMailSubjects({
        query: `?pagination=true&page=${this.page}&limit=${this.limit}`,
      });

      this.totalItems = links;
      this.mailSubjects = data;
    } catch {
      this.mailSubjects = [];
      this.totalItems = 0;
      this.page = 1;
    }

    this.isLoading = false;
  }

  private created() {
    this.clientAppsList({ query: "?pagination=false" });
    // @ts-ignore
    this.filterData = debounce(this.filterData.bind(this), 300);
  }
}
